import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import { ChevronRight } from 'lucide-react'
import { useTopbarSlots } from '@/components/layout/topbar'
import styles from './PageHeader.module.scss'

export interface Crumb {
  label: string
  to?: string
}

export interface PageHeaderProps {
  title: ReactNode
  crumbs?: Crumb[]
  description?: ReactNode
  actions?: ReactNode
  className?: string
}

export function PageHeader({ title, crumbs, description, actions, className }: PageHeaderProps) {
  const slots = useTopbarSlots()
  const titleBlock = (
    <div className={styles.titles}>
      {crumbs && crumbs.length > 0 && (
        <nav className={styles.crumbs} aria-label="Breadcrumb">
          {crumbs.map((c, i) => (
            <span key={i} style={{ display: 'contents' }}>
              {i > 0 && <ChevronRight size={11} />}
              {c.to ? <Link to={c.to}>{c.label}</Link> : <span>{c.label}</span>}
            </span>
          ))}
        </nav>
      )}
      <h1 className={styles.title}>{title}</h1>
    </div>
  )

  // Inside the app shell the title and actions live in the top bar; only the description stays in the page.
  if (slots?.title) {
    return (
      <>
        {createPortal(titleBlock, slots.title)}
        {actions && slots.actions && createPortal(<div className={styles.actions}>{actions}</div>, slots.actions)}
        {description && <p className={clsx(styles.description, styles.descriptionStandalone)}>{description}</p>}
      </>
    )
  }

  return (
    <div className={clsx(styles.header, className)}>
      <div className={styles.titles}>
        {titleBlock}
        {description && <p className={styles.description}>{description}</p>}
      </div>
      {actions && <div className={styles.actions}>{actions}</div>}
    </div>
  )
}

export function Toolbar({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={clsx(styles.toolbar, className)}>{children}</div>
}

export function ToolbarSpacer() {
  return <div className={styles.toolbarSpacer} />
}

export function SectionHeader({
  title,
  sub,
  actions,
  className,
}: {
  title: ReactNode
  sub?: ReactNode
  actions?: ReactNode
  className?: string
}) {
  return (
    <div className={clsx(styles.section, className)}>
      <h2 className={styles.sectionTitle}>
        {title}
        {sub && <span className={styles.sectionSub}>{sub}</span>}
      </h2>
      {actions}
    </div>
  )
}
